/**
 * Text measurement, and the cache in front of it.
 *
 * The architecture names this as the single most common cause of a janky map editor, and the
 * reason is worth stating plainly: measuring text means asking the browser to lay it out, which
 * means a synchronous reflow. Do it once per node per frame on a 2,000-node map and the editor
 * stops responding to the keyboard.
 *
 * So it is done once per *distinct string*, cached by everything that could change the answer —
 * `text | font | size | weight | italic | maxWidth`. Two nodes with the same label measure once.
 * Re-rendering a node whose text did not change measures zero times.
 *
 * Measurement itself is a port. A browser answers with a canvas context, a Web Worker with an
 * OffscreenCanvas, React Native with its own text engine, and a test with a function that counts
 * characters. None of them belong in this file.
 */
export const DEFAULT_TEXT_STYLE = {
    fontFamily: 'ui-sans-serif, system-ui, sans-serif',
    fontSize: 14,
    fontWeight: 400,
    italic: false,
};
/**
 * How many distinct strings are remembered.
 *
 * A cache without a bound is a leak with a slow fuse: a long session renaming nodes accumulates
 * an entry per version of every label ever typed. Ten thousand is far more than any one viewport
 * needs and small enough to be uninteresting in memory.
 */
export const DEFAULT_MEASURE_CACHE_SIZE = 10_000;
function cacheKey(text, style, maxWidth) {
    // NUL as the separator, because it is the one character a node's text cannot contain — a
    // space or a pipe would make ("a b", 12) and ("a", "b 12") the same key.
    return [
        text,
        style.fontFamily,
        String(style.fontSize),
        String(style.fontWeight),
        style.italic ? '1' : '0',
        String(maxWidth),
    ].join('\u0000');
}
export function createMeasureCache(measurer, maxEntries = DEFAULT_MEASURE_CACHE_SIZE) {
    // A `Map` iterates in insertion order, so the oldest key is the first one — which is all a
    // FIFO eviction needs. True LRU would mean re-inserting on every hit, and paying that on the
    // hot path to evict slightly better is the wrong trade for a bounded cache this size.
    const cache = new Map();
    let hits = 0;
    let misses = 0;
    return {
        measure(text, style, maxWidth) {
            const key = cacheKey(text, style, maxWidth);
            const cached = cache.get(key);
            if (cached !== undefined) {
                hits += 1;
                return cached;
            }
            misses += 1;
            const measured = measurer.measure(text, style, maxWidth);
            cache.set(key, measured);
            if (cache.size > maxEntries) {
                const oldest = cache.keys().next();
                if (oldest.done !== true)
                    cache.delete(oldest.value);
            }
            return measured;
        },
        stats: () => ({ size: cache.size, hits, misses }),
        clear: () => {
            cache.clear();
            hits = 0;
            misses = 0;
        },
    };
}
/**
 * Wraps text to a width, given a function that measures a single line.
 *
 * Shared by every adapter, because the *wrapping* rule must not differ between the browser that
 * draws a map and the exporter that renders it to PDF — a line that breaks differently in an
 * export is a document that does not match what the user approved.
 *
 * Breaks on spaces, and breaks inside a word only when the word alone exceeds the width. A URL
 * that overflows its node is worse than one that is split.
 */
export function wrapText(text, maxWidth, widthOf) {
    const lines = [];
    for (const paragraph of text.split('\n')) {
        // A blank line is a blank line. Collapsing it would silently rewrite the user's text.
        if (paragraph === '') {
            lines.push('');
            continue;
        }
        lines.push(...wrapParagraph(paragraph, maxWidth, widthOf));
    }
    return lines;
}
function wrapParagraph(paragraph, maxWidth, widthOf) {
    const lines = [];
    let current = '';
    const flush = () => {
        if (current !== '')
            lines.push(current);
        current = '';
    };
    for (const word of paragraph.split(' ')) {
        const candidate = current === '' ? word : `${current} ${word}`;
        if (widthOf(candidate) <= maxWidth) {
            current = candidate;
            continue;
        }
        // The word does not fit after what is already on the line. Start a new line first — the word
        // may well fit on one of its own.
        flush();
        if (widthOf(word) <= maxWidth) {
            current = word;
            continue;
        }
        // It does not fit on a line of its own either, so it has to be split. Every piece but the
        // last is a complete line; the last becomes what the next word appends to.
        const pieces = splitWord(word, maxWidth, widthOf);
        lines.push(...pieces.slice(0, -1));
        current = pieces[pieces.length - 1] ?? '';
    }
    flush();
    // A paragraph of nothing but spaces produces no lines at all, and a node has to render
    // *something* or its box collapses.
    return lines.length === 0 ? [''] : lines;
}
/**
 * Splits a word wider than the line into pieces that fit.
 *
 * At least one character per piece even when a single character overflows, because the
 * alternative is a loop that never advances.
 */
function splitWord(word, maxWidth, widthOf) {
    const pieces = [];
    let current = '';
    for (const character of word) {
        const candidate = current + character;
        if (current !== '' && widthOf(candidate) > maxWidth) {
            pieces.push(current);
            current = character;
            continue;
        }
        current = candidate;
    }
    pieces.push(current);
    return pieces;
}
/**
 * A measurer for tests and for headless environments.
 *
 * Every character is `0.6 × fontSize` wide, which is roughly right for a proportional sans-serif
 * and exactly predictable — a test asserting a wrap can state the expected answer rather than
 * discovering it. Never used in a browser: real glyph widths are not uniform, and a layout built
 * on this one would be visibly wrong.
 */
export function createApproximateMeasurer(widthRatio = 0.6) {
    return {
        measure(text, style, maxWidth) {
            const widthOf = (line) => line.length * style.fontSize * widthRatio;
            const lines = wrapText(text, maxWidth, widthOf);
            return {
                width: Math.min(maxWidth, Math.max(...lines.map(widthOf), 0)),
                // 1.35 line height: tight enough to look deliberate, loose enough to read.
                height: lines.length * style.fontSize * 1.35,
                lines,
            };
        },
    };
}
//# sourceMappingURL=measure.js.map